import axios from 'axios';
import { CATEGORY_GROUPS, CATEGORY_GROUP_LABELS, type CategoryGroup } from '../types/prospecting';

export { CATEGORY_GROUPS, CATEGORY_GROUP_LABELS };

const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';
const YELP_API_KEY = process.env.REACT_APP_YELP_API_KEY || '';

// Cost guardrails (env-driven)
// Google Places is ON by default; set REACT_APP_DISABLE_GOOGLE_PLACES=true to turn it off
const DISABLE_GOOGLE_PLACES = String(process.env.REACT_APP_DISABLE_GOOGLE_PLACES || 'false').toLowerCase() === 'true';
const MAX_RADIUS_MI = Math.max(1, Number(process.env.REACT_APP_MAX_RADIUS_MI || '30'));
const MAX_PLACE_DETAILS = Math.max(0, Number(process.env.REACT_APP_MAX_PLACE_DETAILS || '10'));
const MOCK_SEARCH = String(process.env.REACT_APP_MOCK_SEARCH || 'false').toLowerCase() === 'true';

const CATEGORY_TO_GOOGLE_TYPES: Record<CategoryGroup, string[]> = {
  agency_services: ['marketing_agency'],
  legal: ['lawyer'],
  dental: ['dentist'],
  medical: ['doctor', 'hospital', 'medical_lab', 'physiotherapist'],
  vision: ['optometrist'],
  hearing: ['hearing_aid_store', 'audiologist'],
  insurance: ['insurance_agency'],
  financial: ['bank', 'credit_union', 'accounting', 'financial_planner'],
  automotive: ['car_dealer', 'car_repair', 'car_wash', 'auto_parts_store'],
  furniture: ['furniture_store'],
  mattress: ['mattress_store', 'furniture_store'],
  home_services: ['general_contractor', 'cleaning_service', 'landscaper', 'painter'],
  hvac: ['hvac_contractor', 'air_conditioning_contractor', 'heating_contractor', 'general_contractor'],
  plumbing: ['plumber'],
  electrical: ['electrician'],
  roofing: ['roofing_contractor', 'general_contractor'],
  flooring: ['flooring_store', 'home_goods_store', 'general_contractor'],
  beauty_wellness: ['beauty_salon', 'hair_care', 'spa', 'massage_therapist'],
  fitness: ['gym'],
  education_childcare: ['school', 'college', 'university', 'preschool', 'child_care_agency'],
  senior_living: ['nursing_home', 'retirement_community', 'assisted_living_facility']
};

const CATEGORY_VERTICAL_KEYWORDS: Partial<Record<CategoryGroup, string[]>> = {
  agency_services: [
    'marketing agency',
    'digital marketing agency',
    'seo agency',
    'web design agency',
    'advertising agency',
    'branding agency',
    'public relations agency',
    'AI marketing consultant'
  ],
  legal: ['attorney', 'law firm', 'personal injury attorney'],
  dental: ['dentist', 'dental'],
  medical: ['doctor', 'medical clinic'],
  vision: ['optometrist', 'eye care'],
  hearing: ['audiology', 'hearing aid'],
  insurance: ['insurance agency'],
  financial: ['financial advisor', 'accounting'],
  automotive: ['auto dealer', 'auto repair'],
  furniture: ['furniture store'],
  mattress: ['mattress store'],
  home_services: ['home services contractor'],
  hvac: ['hvac contractor'],
  plumbing: ['plumber'],
  electrical: ['electrician'],
  roofing: ['roofing contractor'],
  flooring: ['flooring store'],
  beauty_wellness: ['beauty salon', 'spa'],
  fitness: ['gym', 'fitness center'],
  education_childcare: ['child care', 'learning center'],
  senior_living: ['assisted living', 'senior living']
};

const GOOGLE_TYPE_TO_CATEGORY_GROUP: Partial<Record<string, CategoryGroup>> = Object.entries(CATEGORY_TO_GOOGLE_TYPES)
  .reduce((acc, [group, types]) => {
    types.forEach((type) => {
      if (!acc[type]) acc[type] = group as CategoryGroup;
    });
    return acc;
  }, {} as Partial<Record<string, CategoryGroup>>);

export interface RealBusiness {
  id: string;
  name: string;
  category: string;
  categoryGroup?: CategoryGroup;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  website: string;
  lat: number;
  lng: number;
  openNow: boolean;
  reviews: number;
  rating?: number;
  priceLevel?: number;
  photos?: string[];
  placeId?: string;
  source: 'google' | 'yelp' | 'osm';
}

const getCategoryLabel = (group?: CategoryGroup, fallbackType?: string): string => {
  if (group) return CATEGORY_GROUP_LABELS[group];
  return formatBusinessType(fallbackType || 'establishment');
};

const inferCategoryGroup = (types: string[] | undefined): CategoryGroup | undefined => {
  if (!Array.isArray(types)) return undefined;
  return types.map((type) => GOOGLE_TYPE_TO_CATEGORY_GROUP[type]).find(Boolean);
};

type SearchGooglePlacesOptions = {
  exhaustive?: boolean;
  keywords?: string[];
};

const toRadians = (value: number): number => (value * Math.PI) / 180;

const distanceMilesBetween = (aLat: number, aLng: number, bLat: number, bLng: number): number => {
  const dLat = toRadians(bLat - aLat);
  const dLng = toRadians(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(aLat)) * Math.cos(toRadians(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 3958.8 * Math.asin(Math.sqrt(h));
};

const milesToLatDegrees = (miles: number): number => miles / 69;

const milesToLngDegrees = (miles: number, lat: number): number => {
  const cosLat = Math.cos(toRadians(lat));
  if (Math.abs(cosLat) < 0.0001) return 0;
  return miles / (69 * cosLat);
};

const buildSearchCenters = (
  lat: number,
  lng: number,
  radiusMiles: number,
  exhaustive: boolean
): Array<{ lat: number; lng: number }> => {
  if (!exhaustive || radiusMiles < 3) return [{ lat, lng }];

  const stepMiles = Math.min(Math.max(radiusMiles / 2.5, 2.5), 4.5);
  const latStep = milesToLatDegrees(stepMiles);
  const lngStep = milesToLngDegrees(stepMiles, lat);
  const offsets = [-1, 0, 1];
  const centers: Array<{ lat: number; lng: number }> = [];

  offsets.forEach((latOffset) => {
    offsets.forEach((lngOffset) => {
      centers.push({
        lat: lat + latOffset * latStep,
        lng: lng + lngOffset * lngStep
      });
    });
  });

  return centers;
};

const nearbySearchRequest = async (
  service: google.maps.places.PlacesService,
  request: google.maps.places.PlaceSearchRequest
): Promise<any[]> => {
  return new Promise<any[]>((resolve) => {
    service.nearbySearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
        resolve(results as any[]);
      } else {
        resolve([]);
      }
    });
  });
};

const textSearchRequest = async (
  service: google.maps.places.PlacesService,
  request: google.maps.places.TextSearchRequest
): Promise<any[]> => {
  return new Promise<any[]>((resolve) => {
    service.textSearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
        resolve(results as any[]);
      } else {
        resolve([]);
      }
    });
  });
};

// Helper function to format Google business types into readable categories
const formatBusinessType = (type: string): string => {
  const typeMap: Record<string, string> = {
    // Auto & Transportation
    'car_dealer': 'Auto Dealer',
    'car_repair': 'Auto Repair',
    'auto_parts_store': 'Auto Parts',
    'car_wash': 'Car Wash',
    'gas_station': 'Gas Station',
    
    // Medical & Health
    'dentist': 'Dental',
    'doctor': 'Medical',
    'hospital': 'Medical',
    'medical_lab': 'Medical',
    'pharmacy': 'Pharmacy',
    'veterinary_care': 'Veterinary',
    'physiotherapist': 'Physical Therapy',
    'optometrist': 'Vision',
    'hearing_aid_store': 'Hearing',
    'audiologist': 'Hearing',
    'nursing_home': 'Senior Living',
    'retirement_community': 'Senior Living',
    'assisted_living_facility': 'Senior Living',
    
    // Professional Services
    'lawyer': 'Legal Services',
    'accounting': 'Accounting',
    'real_estate_agency': 'Real Estate',
    'insurance_agency': 'Insurance',
    'travel_agency': 'Travel Agency',
    
    // Food & Dining
    'restaurant': 'Restaurant',
    'food': 'Restaurant',
    'bakery': 'Bakery',
    'cafe': 'Cafe',
    'bar': 'Bar',
    'liquor_store': 'Liquor Store',
    
    // Retail & Shopping
    'store': 'Retail',
    'clothing_store': 'Clothing Store',
    'jewelry_store': 'Jewelry Store',
    'shoe_store': 'Shoe Store',
    'book_store': 'Book Store',
    'electronics_store': 'Electronics',
    'home_goods_store': 'Home & Garden',
    'furniture_store': 'Furniture',
    'hardware_store': 'Hardware Store',
    'convenience_store': 'Convenience Store',
    'department_store': 'Department Store',
    'shopping_mall': 'Shopping Mall',
    
    // Financial
    'bank': 'Financial',
    'credit_union': 'Credit Union',
    'atm': 'ATM',
    'financial_planner': 'Financial Planning',
    
    // Home Services
    'plumber': 'Plumbing',
    'electrician': 'Electrical',
    'general_contractor': 'Contractor',
    'hvac_contractor': 'HVAC',
    'air_conditioning_contractor': 'HVAC',
    'heating_contractor': 'HVAC',
    'roofing_contractor': 'Roofing',
    'painter': 'Painting',
    'landscaper': 'Landscaping',
    'moving_company': 'Moving Company',
    'cleaning_service': 'Cleaning Service',
    'flooring_store': 'Flooring',
    'mattress_store': 'Mattress',
    
    // Personal Care
    'hair_care': 'Beauty/Hair',
    'beauty_salon': 'Beauty/Hair',
    'spa': 'Spa/Wellness',
    'gym': 'Fitness',
    'massage_therapist': 'Massage',
    
    // Entertainment
    'movie_theater': 'Movie Theater',
    'bowling_alley': 'Bowling',
    'amusement_park': 'Amusement Park',
    'museum': 'Museum',
    'art_gallery': 'Art Gallery',
    
    // Hotels & Lodging
    'lodging': 'Hotel',
    'hotel': 'Hotel',
    'motel': 'Motel',
    
    // Education
    'university': 'University',
    'college': 'College',
    'school': 'School',
    'preschool': 'Childcare',
    'child_care_agency': 'Childcare',
    
    // Other
    'establishment': 'Business',
    'point_of_interest': 'Business',
    'local_government_office': 'Government Office'
  };
  
  return typeMap[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Google Places API using JavaScript Library (avoids CORS)
export const searchGooglePlaces = async (
  lat: number, 
  lng: number, 
  radius: number,
  types: string[] = ['establishment'],
  options: SearchGooglePlacesOptions = {}
): Promise<RealBusiness[]> => {
  // Optional hard switch to fully disable client Places to avoid costs
  if (DISABLE_GOOGLE_PLACES) {
    console.warn('🔒 Google Places disabled via REACT_APP_DISABLE_GOOGLE_PLACES');
    return [];
  }
  console.log('🔑 Checking Google Places API key...');
  if (!GOOGLE_PLACES_API_KEY) {
    console.warn('🔑 Google Places API key not configured - using demo data');
    return [];
  }
  console.log('🔑 Google Places API key found:', GOOGLE_PLACES_API_KEY.substring(0, 10) + '...');
  
  // Test if the API key is valid
  if (GOOGLE_PLACES_API_KEY === 'your_key_here' || GOOGLE_PLACES_API_KEY.length < 10) {
    console.warn('🔑 Google Places API key appears to be invalid or placeholder');
    return [];
  }

  return new Promise(async (resolve) => {
    // Check if Google Maps API is loaded
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.warn('⚠️ Google Maps Places library not loaded');
      console.warn('⚠️ This usually means the Google Maps API key is invalid or not configured');
      resolve([]);
      return;
    }

    console.log('✅ Google Maps API is loaded and ready');

    try {
      console.log('🔍 Searching Google Places with multiple queries...');
      
      // Create a temporary map element (required for PlacesService)
      const mapDiv = document.createElement('div');
      const map = new window.google.maps.Map(mapDiv, {
        center: { lat, lng },
        zoom: 15
      });

      const service = new window.google.maps.places.PlacesService(map);
      // Clamp radius to avoid overly broad scans
      const safeRadiusMi = Math.min(Math.max(0.5, radius), MAX_RADIUS_MI);
      const radiusInMeters = Math.min(safeRadiusMi * 1609.34, 50000); // Convert miles to meters, max 50km
      console.log(`🔍 Searching radius: ${radius} miles (${radiusInMeters} meters)`);

      const exhaustive = options.exhaustive === true;
      const searchCenters = buildSearchCenters(lat, lng, safeRadiusMi, exhaustive);
      const keywordQueries = Array.from(new Set((options.keywords || []).map((keyword) => String(keyword || '').trim()).filter(Boolean)));

      // Build query list from provided types (deduped); fallback to establishment only
      const dedupTypes = Array.from(new Set((types || []).filter(Boolean)));
      const baseTypes = dedupTypes.length > 0 ? dedupTypes : ['establishment'];
      const searchQueries = baseTypes.map((t) => ({ type: t }));

      const allResults: any[] = [];
      
      const nearbyRadiusMeters = Math.min(
        exhaustive ? Math.max(radiusInMeters / 2, 2500) : radiusInMeters,
        exhaustive ? 9000 : radiusInMeters
      );

      for (const centerPoint of searchCenters) {
        for (const query of searchQueries) {
          try {
            const request = {
              location: new window.google.maps.LatLng(centerPoint.lat, centerPoint.lng),
              radius: nearbyRadiusMeters,
              type: query.type
            };

            const results = await nearbySearchRequest(service, request);
            allResults.push(...results);
            console.log(`✅ ${query.type} @ ${centerPoint.lat.toFixed(3)},${centerPoint.lng.toFixed(3)}: ${results.length} businesses`);
            await new Promise(resolve => setTimeout(resolve, 80));
          } catch (error) {
            console.warn(`⚠️ Search failed for ${query.type}:`, error);
          }
        }

        for (const keyword of keywordQueries) {
          try {
            const request = {
              query: keyword,
              location: new window.google.maps.LatLng(centerPoint.lat, centerPoint.lng),
              radius: radiusInMeters
            };
            const results = await textSearchRequest(service, request);
            allResults.push(...results);
            console.log(`✅ text "${keyword}" @ ${centerPoint.lat.toFixed(3)},${centerPoint.lng.toFixed(3)}: ${results.length} businesses`);
            await new Promise(resolve => setTimeout(resolve, 80));
          } catch (error) {
            console.warn(`⚠️ Text search failed for ${keyword}:`, error);
          }
        }
      }

      // Remove duplicates based on place_id
      const uniqueResults = allResults.filter((place, index, self) => 
        index === self.findIndex(p => p.place_id === place.place_id)
      );

      // Strict post-filter: keep only results whose types intersect requested baseTypes
      const allowedTypeSet = new Set(baseTypes);
      const filteredByRadius = uniqueResults.filter((place: any) => {
        const placeLat = typeof place.geometry?.location?.lat === 'function' ? place.geometry.location.lat() : place.geometry?.location?.lat;
        const placeLng = typeof place.geometry?.location?.lng === 'function' ? place.geometry.location.lng() : place.geometry?.location?.lng;
        if (!Number.isFinite(placeLat) || !Number.isFinite(placeLng)) return false;
        return distanceMilesBetween(lat, lng, placeLat, placeLng) <= safeRadiusMi;
      });

      const filteredResults = (allowedTypeSet.size === 1 && allowedTypeSet.has('establishment') && keywordQueries.length === 0)
        ? uniqueResults // no categories selected → allow all
        : filteredByRadius.filter((place: any) => {
          const typeMatch = Array.isArray(place.types) && place.types.some((t: string) => allowedTypeSet.has(t));
          const keywordText = `${place.name || ''} ${place.formatted_address || ''} ${place.vicinity || ''}`.toLowerCase();
          const keywordMatch = keywordQueries.length > 0 && keywordQueries.some((keyword) => keywordText.includes(keyword.toLowerCase()));
          return typeMatch || keywordMatch;
        });

      console.log(`✅ Total unique Google Places found: ${uniqueResults.length} businesses; after metro filter: ${filteredResults.length}`);
      
      const businesses: RealBusiness[] = filteredResults
        .filter((place: any) => {
          // Only filter out incomplete results - let AI handle ALL the decisions
          if (!place.name || !place.geometry?.location) return false;
          
          // Let everything through - AI will decide what's valuable
          return true;
        })
        .map((place: any) => {
          // Parse address components
          const vicinity = place.vicinity || '';
          const addressParts = vicinity.split(', ');
          
          let city = '';
          let state = '';
          let address = vicinity;
          
          if (addressParts.length >= 2) {
            address = addressParts[0];
            city = addressParts[1];
            if (addressParts.length >= 3) {
              state = addressParts[2];
            }
          }

          const categoryGroup = inferCategoryGroup(place.types);
          return {
            id: place.place_id || `google_${Math.random().toString(36).substr(2, 9)}`,
            name: place.name || 'Unknown Business',
            category: getCategoryLabel(categoryGroup, place.types?.[0] || 'establishment'),
            categoryGroup,
            address: address || vicinity || '',
            city: city || 'Birmingham', // Default to Birmingham for Alabama
            state: state || 'AL',
            zip: '', // Would need Place Details API for accurate zip
            phone: '', // Would need Place Details API for phone
            website: '', // Would need Place Details API for website
            lat: place.geometry?.location?.lat() || lat,
            lng: place.geometry?.location?.lng() || lng,
            openNow: place.opening_hours?.open_now ?? true,
            reviews: place.user_ratings_total || 0,
            rating: place.rating,
            priceLevel: place.price_level,
            photos: place.photos?.slice(0, 3).map((photo: any) => 
              photo.getUrl({ maxWidth: 400, maxHeight: 400 })
            ) || [],
            placeId: place.place_id,
            source: 'google' as const
          };
        });

      console.log(`🎯 Processed ${businesses.length} valid businesses`);
      resolve(businesses);
    } catch (error) {
      console.error('❌ Google Places API error:', error);
      resolve([]);
    }
  });
};

// Get detailed place information (requires separate API call)
export const getPlaceDetails = async (placeId: string): Promise<Partial<RealBusiness>> => {
  if (!GOOGLE_PLACES_API_KEY || !window.google?.maps?.places) {
    return {};
  }

  return new Promise((resolve) => {
    const mapDiv = document.createElement('div');
    const map = new window.google.maps.Map(mapDiv, { center: { lat: 0, lng: 0 }, zoom: 1 });
    const service = new window.google.maps.places.PlacesService(map);

    const request = {
      placeId: placeId,
      fields: ['formatted_address', 'formatted_phone_number', 'international_phone_number', 'website', 'address_components']
    };

    service.getDetails(request, (place: any, status: any) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        const addressComponents = place.address_components || [];
        
        resolve({
          address: place.formatted_address || '',
          phone: place.formatted_phone_number || place.international_phone_number || '',
          website: place.website || '',
          city: addressComponents.find((comp: any) => 
            comp.types.includes('locality')
          )?.long_name || '',
          state: addressComponents.find((comp: any) => 
            comp.types.includes('administrative_area_level_1')
          )?.short_name || '',
          zip: addressComponents.find((comp: any) => 
            comp.types.includes('postal_code')
          )?.long_name || ''
        });
      } else {
        resolve({});
      }
    });
  });
};

// Quick lookup by name (bias near lat/lng). OSM-first, optional Google.
export const lookupBusinessByName = async (
  query: string,
  lat: number,
  lng: number,
  searchRadiusMi: number = 2
): Promise<RealBusiness | null> => {
  // Mock mode: return a stub around center
  if (MOCK_SEARCH) {
    return {
      id: 'mock_lookup_1',
      name: query,
      category: 'Business',
      address: '100 Test St',
      city: 'Birmingham',
      state: 'AL',
      zip: '35203',
      phone: '',
      website: '',
      lat,
      lng,
      openNow: true,
      reviews: 0,
      source: 'google'
    };
  }

  // Google client-side NearbySearch/TextSearch with strict distance filter
  if (window.google?.maps?.places) {
    try {
      const mapDiv = document.createElement('div');
      const map = new window.google.maps.Map(mapDiv, { center: { lat, lng }, zoom: 14 });
      const service = new window.google.maps.places.PlacesService(map);
      const radiusMeters = Math.min(Math.max(800, searchRadiusMi * 1609.34), 20000); // clamp 0.5mi..12mi

      // Nearby search with keyword is stricter to radius
      const nearby: any[] = await new Promise((resolve) => {
        const req: any = { location: new window.google.maps.LatLng(lat, lng), radius: radiusMeters, keyword: query };
        service.nearbySearch(req, (res: any[], status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && res) resolve(res);
          else resolve([]);
        });
      });

      let r = nearby[0];
      if (!r) {
        // Fallback to text search if nearby returned nothing
        const textRes: any[] = await new Promise((resolve) => {
          const req: any = { query, location: new window.google.maps.LatLng(lat, lng), radius: radiusMeters, fields: ['place_id','name','geometry','formatted_address','types'] };
          service.textSearch(req, (res: any[], status: any) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && res) resolve(res);
            else resolve([]);
          });
        });
        // choose nearest within 1.5x radius, else first
        const toMiles = (b: any) => {
          const glat = typeof b.geometry?.location?.lat === 'function' ? b.geometry.location.lat() : b.geometry?.location?.lat;
          const glng = typeof b.geometry?.location?.lng === 'function' ? b.geometry.location.lng() : b.geometry?.location?.lng;
          const toRad = (v: number) => (v * Math.PI) / 180;
          const dLat = toRad(glat - lat);
          const dLng = toRad(glng - lng);
          const R = 3958.8;
          const h = Math.sin(dLat/2)**2 + Math.cos(toRad(lat))*Math.cos(toRad(glat))*Math.sin(dLng/2)**2;
          return 2 * R * Math.asin(Math.sqrt(h));
        };
        const sorted = textRes.map(b => ({ b, d: toMiles(b) })).sort((a,b) => a.d - b.d);
        r = (sorted.find(x => x.d <= searchRadiusMi) || null)?.b;
      }
      if (r) {
        // Compute distance and enforce strict within-radius filter
        const glat = typeof r.geometry?.location?.lat === 'function' ? r.geometry.location.lat() : r.geometry?.location?.lat;
        const glng = typeof r.geometry?.location?.lng === 'function' ? r.geometry.location.lng() : r.geometry?.location?.lng;
        const toRad = (v: number) => (v * Math.PI) / 180;
        const dLat = toRad(glat - lat);
        const dLng = toRad(glng - lng);
        const R = 3958.8; // miles
        const h = Math.sin(dLat/2)**2 + Math.cos(toRad(lat))*Math.cos(toRad(glat))*Math.sin(dLng/2)**2;
        const distMi = 2 * R * Math.asin(Math.sqrt(h));
        if (distMi > searchRadiusMi) {
          return null;
        }
        const categoryGroup = inferCategoryGroup(r.types);
        return {
          id: r.place_id,
          name: r.name || query,
          category: getCategoryLabel(categoryGroup, r.types?.[0] || 'establishment'),
          categoryGroup,
          address: r.vicinity || r.formatted_address || '',
          city: '',
          state: '',
          zip: '',
          phone: '',
          website: '',
          lat: (typeof r.geometry?.location?.lat === 'function' ? r.geometry.location.lat() : r.geometry?.location?.lat) || lat,
          lng: (typeof r.geometry?.location?.lng === 'function' ? r.geometry.location.lng() : r.geometry?.location?.lng) || lng,
          openNow: true,
          reviews: r.user_ratings_total || 0,
          rating: r.rating,
          placeId: r.place_id,
          source: 'google'
        };
      }
    } catch {}
  }

  return null;
};

// Category lookup within radius (OSM-first, Google optional), returns multiple
export const lookupCategoryWithinRadius = async (
  category: string,
  lat: number,
  lng: number,
  radiusMi: number,
  limit: number = 20
): Promise<RealBusiness[]> => {
  const results: RealBusiness[] = [];
  const toMiles = (aLat: number, aLng: number, bLat: number, bLng: number) => {
    const toRad = (v: number) => (v * Math.PI) / 180;
    const dLat = toRad(bLat - aLat);
    const dLng = toRad(bLng - aLng);
    const R = 3958.8;
    const h = Math.sin(dLat/2)**2 + Math.cos(toRad(aLat))*Math.cos(toRad(bLat))*Math.sin(dLng/2)**2;
    return 2 * R * Math.asin(Math.sqrt(h));
  };
  // Google only
  if (window.google?.maps?.places) {
    try {
      const mapDiv = document.createElement('div');
      const map = new window.google.maps.Map(mapDiv, { center: { lat, lng }, zoom: 14 });
      const service = new window.google.maps.places.PlacesService(map);
      const radiusMeters = Math.min(Math.max(800, radiusMi * 1609.34), 20000);
      const nearby: any[] = await new Promise((resolve) => {
        const req: any = { location: new window.google.maps.LatLng(lat, lng), radius: radiusMeters, keyword: category };
        service.nearbySearch(req, (res: any[], status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && res) resolve(res);
          else resolve([]);
        });
      });
      nearby.forEach((r) => {
        const glat = typeof r.geometry?.location?.lat === 'function' ? r.geometry.location.lat() : r.geometry?.location?.lat;
        const glng = typeof r.geometry?.location?.lng === 'function' ? r.geometry.location.lng() : r.geometry?.location?.lng;
        const dist = toMiles(lat, lng, glat, glng);
        if (dist <= radiusMi && results.length < limit) {
          const categoryGroup = inferCategoryGroup(r.types);
          results.push({
            id: r.place_id,
            name: r.name,
            category: getCategoryLabel(categoryGroup, r.types?.[0] || 'establishment'),
            categoryGroup,
            address: r.vicinity || '',
            city: '', state: '', zip: '', phone: '', website: '',
            lat: glat, lng: glng,
            openNow: r.opening_hours?.open_now ?? true,
            reviews: r.user_ratings_total || 0,
            rating: r.rating,
            placeId: r.place_id,
            source: 'google'
          });
        }
      });
    } catch {}
  }

  return results.slice(0, limit);
};

// Yelp API (requires CORS proxy in production)
export const searchYelpBusinesses = async (
  lat: number,
  lng: number,
  radius: number,
  categories: string[] = []
): Promise<RealBusiness[]> => {
  if (!YELP_API_KEY || YELP_API_KEY === 'your_key_here') {
    console.warn('Yelp API key not configured');
    return [];
  }

  try {
    const radiusInMeters = Math.min(radius * 1609.34, 40000); // Yelp max is 40km
    
    const response = await axios.get('https://api.yelp.com/v3/businesses/search', {
      headers: {
        'Authorization': `Bearer ${YELP_API_KEY}`,
        'Accept': 'application/json',
      },
      params: {
        latitude: lat,
        longitude: lng,
        radius: radiusInMeters,
        categories: categories.length > 0 ? categories.join(',') : undefined,
        limit: 50
      }
    });

    return response.data.businesses.map((business: any) => ({
      id: `yelp_${business.id}`,
      name: business.name,
      category: business.categories?.[0]?.title || 'Business',
      address: business.location?.address1 || '',
      city: business.location?.city || '',
      state: business.location?.state || '',
      zip: business.location?.zip_code || '',
      phone: business.phone || '',
      website: business.url || '',
      lat: business.coordinates?.latitude || lat,
      lng: business.coordinates?.longitude || lng,
      openNow: !business.is_closed,
      reviews: business.review_count || 0,
      rating: business.rating,
      photos: business.photos || [],
      source: 'yelp' as const
    }));

  } catch (error) {
    console.error('Yelp API error:', error);
    return [];
  }
};

// OpenStreetMap Nominatim API (free, no key required)
export const searchOSMBusinesses = async (
  lat: number,
  lng: number,
  radius: number
): Promise<RealBusiness[]> => {
  try {
    // Convert radius to bounding box
    const latOffset = radius / 69; // Roughly 1 degree = 69 miles
    const lngOffset = radius / (69 * Math.cos(lat * Math.PI / 180));
    
    const bbox = [
      lng - lngOffset, // west
      lat - latOffset, // south
      lng + lngOffset, // east
      lat + latOffset  // north
    ].join(',');

    const response = await axios.get('https://nominatim.openstreetmap.org/search', {
      params: {
        format: 'json',
        limit: 50,
        bounded: 1,
        viewbox: bbox,
        extratags: 1,
        namedetails: 1,
        addressdetails: 1,
        q: 'shop OR amenity OR office OR commercial'
      },
      headers: {
        'User-Agent': 'rAEdar Business Finder'
      }
    });

    return response.data
      .filter((place: any) => place.lat && place.lon && place.display_name)
      .map((place: any, index: number) => ({
        id: `osm_${place.osm_id || index}`,
        name: place.display_name.split(',')[0] || 'Unknown Business',
        category: place.type || 'Business',
        address: place.address?.house_number && place.address?.road 
          ? `${place.address.house_number} ${place.address.road}`
          : place.display_name.split(',')[0] || '',
        city: place.address?.city || place.address?.town || place.address?.village || '',
        state: place.address?.state || '',
        zip: place.address?.postcode || '',
        phone: '',
        website: '',
        lat: parseFloat(place.lat),
        lng: parseFloat(place.lon),
        openNow: true, // OSM doesn't provide opening hours in search
        reviews: 0,
        source: 'osm' as const
      }));

  } catch (error) {
    console.error('OpenStreetMap API error:', error);
    return [];
  }
};

// Main search function that combines multiple sources
export const searchRealBusinesses = async (
  lat: number,
  lng: number,
  radius: number,
  limit: number = 20,
  categories?: string[],
  options: { exhaustive?: boolean } = {}
): Promise<RealBusiness[]> => {
  console.log(`🔍 Searching for businesses near (${lat}, ${lng}) within ${radius} miles`);
  
  // Mock mode for zero-cost testing
  if (MOCK_SEARCH) {
    const seed = 6;
    const mk = (i: number, name: string, category: string): RealBusiness => ({
      id: `mock_${i}`,
      name,
      category,
      address: `${100 + i} Test St` ,
      city: 'Birmingham',
      state: 'AL',
      zip: '35203',
      phone: '',
      website: '',
      lat: lat + ((i % seed) - seed/2) * 0.001,
      lng: lng + ((i % seed) - seed/2) * 0.001,
      openNow: true,
      reviews: 0,
      source: 'osm'
    });
      const cats = (categories && categories.length ? categories : CATEGORY_GROUPS as unknown as string[]) as string[];
    const namesByCat: Record<string,string[]> = {
      legal: ['Law Office', 'Attorney Group'],
      dental: ['Acme Dental', 'Smiles Dentistry'],
      medical: ['Health Clinic', 'Primary Care Center'],
      vision: ['Vision Center', 'Eye Care Associates'],
      hearing: ['Hearing Center', 'Audiology Clinic'],
      insurance: ['Insurance Agency', 'Coverage Partners'],
      financial: ['Community Bank', 'Financial Group'],
      automotive: ['Auto Dealer', 'Repair Garage'],
      furniture: ['Furniture Gallery', 'Home Furnishings'],
      mattress: ['Mattress Outlet', 'Sleep Center'],
      home_services: ['Home Services Co', 'Neighborhood Contractors'],
      hvac: ['HVAC Pros', 'Climate Control Co'],
      plumbing: ['Plumbing Experts', 'Rapid Plumbing'],
      electrical: ['Electric Co', 'Power Systems'],
      roofing: ['Roofing Group', 'Roof Repair Co'],
      flooring: ['Flooring Studio', 'Tile & Floor Center'],
      beauty_wellness: ['Salon Studio', 'Day Spa'],
      fitness: ['Fitness Center', 'Training Studio'],
      education_childcare: ['Learning Center', 'Childcare Academy'],
      senior_living: ['Senior Living Center', 'Retirement Community']
    };
    const mock: RealBusiness[] = [];
    cats.forEach((c, idx) => {
      const arr = namesByCat[c] || ['Business'];
      arr.forEach((n, j) => mock.push({ ...mk(idx * 3 + j, n, CATEGORY_GROUP_LABELS[c as CategoryGroup] || c), categoryGroup: c as CategoryGroup }));
    });
    console.log(`🧪 MOCK mode: returning ${mock.length} items`);
    return mock;
  }

  const allBusinesses: RealBusiness[] = [];
  
  try {
    const clampedRadius = Math.min(Math.max(0.5, radius), MAX_RADIUS_MI);
    // Try Google Places first (best data quality), unless disabled
    if (!DISABLE_GOOGLE_PLACES) {
      console.log('🌐 Trying Google Places...');
      // Derive allowed types from category groups
      const selectedGroups = (categories && categories.length ? categories : (CATEGORY_GROUPS as unknown as string[])) as string[];
      const keywordQueries = options.exhaustive
        ? Array.from(
            new Set(
              selectedGroups
                .filter((g): g is CategoryGroup => (CATEGORY_GROUPS as readonly string[]).includes(g))
                .flatMap((g) => [
                  CATEGORY_GROUP_LABELS[g],
                  ...(CATEGORY_VERTICAL_KEYWORDS[g] || [])
                ])
                .filter(Boolean)
            )
          )
        : [];
      const allowedTypes = Array.from(new Set(
        selectedGroups
          .filter((g): g is CategoryGroup => (CATEGORY_GROUPS as readonly string[]).includes(g))
          .flatMap((g) => CATEGORY_TO_GOOGLE_TYPES[g as CategoryGroup])
      ));
      const googleBusinesses = await searchGooglePlaces(lat, lng, clampedRadius, allowedTypes, {
        exhaustive: options.exhaustive,
        keywords: keywordQueries
      });
      if (googleBusinesses.length > 0) {
        allBusinesses.push(...googleBusinesses);
        console.log(`✅ Google Places: ${googleBusinesses.length} businesses`);
      }
    } else {
      console.log('🔒 Skipping Google Places (disabled by env)');
    }
    
    // If we don't have enough results, try OSM as backup
    if (allBusinesses.length < limit) {
      console.log('🗺️ Adding OpenStreetMap results...');
      const osmBusinesses = await searchOSMBusinesses(lat, lng, clampedRadius);
      
      // Filter out duplicates based on name and approximate location
      const uniqueOSM = osmBusinesses.filter(osmBiz => 
        !allBusinesses.some(existing => 
          existing.name.toLowerCase() === osmBiz.name.toLowerCase() ||
          (Math.abs(existing.lat - osmBiz.lat) < 0.001 && Math.abs(existing.lng - osmBiz.lng) < 0.001)
        )
      );
      
      allBusinesses.push(...uniqueOSM);
      console.log(`✅ OpenStreetMap: ${uniqueOSM.length} unique businesses added`);
    }
    
  } catch (error) {
    console.error('❌ Error in searchRealBusinesses:', error);
  }
  
  // Sort by distance and cap at 500 businesses for quality
  const sorted = allBusinesses
    .map(business => ({
      ...business,
      distance: Math.sqrt(
        Math.pow(business.lat - lat, 2) + Math.pow(business.lng - lng, 2)
      )
    }))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 500); // Cap at 500 for quality analysis
  
  // Enhance businesses with detailed information
  console.log('🔍 Fetching detailed information for businesses...');
  const enhancedBusinesses = await Promise.all(
    sorted.map(async (business, index) => {
      // Limit expensive Google Details lookups
      if (business.source === 'google' && business.placeId && index < MAX_PLACE_DETAILS && !DISABLE_GOOGLE_PLACES) {
        try {
          const details = await getPlaceDetails(business.placeId);
          return { ...business, ...details };
        } catch (error) {
          console.warn(`Could not fetch details for ${business.name}:`, error);
          return business;
        }
      }
      return business;
    })
  );
  
  console.log(`🎯 Returning ${enhancedBusinesses.length} enhanced businesses from real APIs`);
  return enhancedBusinesses;
};

// Enhanced business details fetching
export const getBusinessDetails = async (business: RealBusiness): Promise<RealBusiness> => {
  if (business.source === 'google' && business.placeId) {
    try {
      const details = await getPlaceDetails(business.placeId);
      return { ...business, ...details };
    } catch (error) {
      console.warn('Could not fetch additional details for', business.name);
    }
  }
  
  return business;
};
